import type { RequestConfig } from '@umijs/max';
import { message, notification } from 'antd';
import { history } from '@umijs/max';
import { auth401Handler } from '@/utils/auth401Handler';
import {
  isApiRequestCanceled,
  isNetworkRequestError,
  isRequestTimeoutError,
} from '@/utils/errorHandler';
import { getApiErrorDisplayMessage } from '@/utils/localizeApiError';
import {
  hasTransferCreatePendingLogout,
  isOnTransferCreatePage,
  markTransferCreatePendingLogout,
} from '@/utils/transferCreateLogoutGuard';
import { storeForceLogoutNotice } from '@/utils/forceLogout';
import { authManager, AuthSystem } from '@/services/auth/authManager';
import { shouldIgnoreAdminSession401 } from '@/utils/authSessionGuard';
import { formatLocaleMessage as formatMessage } from '@/utils/formatLocaleMessage';

const isDev = process.env.NODE_ENV === 'development';

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  success: boolean;
  data: any;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
}

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // Configure params serialization for arrays
  paramsSerializer: (params: any) => {
    const searchParams = new URLSearchParams();

    // Special logging for analytics requests
    if (isDev && params.locationIds !== undefined) {
      console.log('[paramsSerializer] Processing locationIds:', {
        value: params.locationIds,
        type: typeof params.locationIds,
        isArray: Array.isArray(params.locationIds),
        length: params.locationIds?.length,
      });
    }

    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        return;
      }

      if (Array.isArray(value)) {
        // For arrays, append each value with the same key
        if (value.length === 0) {
          // For empty arrays, we need to send a special marker so the backend knows it's an empty array
          // Using 'locationIds=' without a value to indicate empty array
          searchParams.append(key, '');
          if (isDev) console.log(`[paramsSerializer] Appending empty array for ${key}`);
        } else {
          value.forEach((item) => {
            searchParams.append(key, String(item));
          });
        }
      } else {
        searchParams.append(key, String(value));
      }
    });

    const queryString = searchParams.toString();
    if (isDev) console.log('[paramsSerializer] Serialized params:', queryString);
    return queryString;
  },

  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const { success, data, errorCode, errorMessage, showType } =
        res as unknown as ResponseStructure;
      if (!success) {
        const error: any = new Error(errorMessage);
        error.name = 'BizError';
        error.info = { errorCode, errorMessage, showType, data };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      // Unregister the request on error
      if (error.config?.url) {
        auth401Handler.unregisterRequest(error.config.url);
      }

      // Skip if this is a cancelled request
      if (error.isCancel || isApiRequestCanceled(error) || error.name === 'AbortError') {
        console.debug('Request cancelled:', error.message);
        return;
      }

      // Check if error handler should be skipped
      if (opts?.skipErrorHandler) throw error;

      // A timeout does not tell us whether the server completed a write.
      if (isRequestTimeoutError(error)) {
        message.error({
          key: 'global-request-timeout',
          content: formatMessage('requestError.timeout'),
        });
        return;
      }

      // Network failures can arrive in several shapes depending on whether
      // Axios, fetch, or the Umi request wrapper created the error. Handle all
      // of them before the response/request branches so concurrent dashboard
      // calls update one notification instead of stacking identical messages.
      if (isNetworkRequestError(error)) {
        message.error({
          key: 'global-network-error',
          content: formatMessage('requestError.network'),
        });
        return;
      }

      // Check if this is a non-critical API call for brand/supplier/customer selectors
      const requestUrl = error.config?.url || '';
      const isNonCriticalApi =
        requestUrl.includes('/api/brand') ||
        requestUrl.includes('/api/mi-brands') ||
        requestUrl.includes('/api/supplier') ||
        requestUrl.includes('/api/customer');

      // For 403 errors on non-critical APIs, don't show notification
      if (error.response?.status === 403 && isNonCriticalApi) {
        throw error;
      }

      // 開發模式下的錯誤信息已移除以保持控制台清潔

      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              message.error(errorMessage);
          }
        }
      } else if (error.response) {
        // Axios 的错误 - 服务器返回了非2xx状态码
        const { status, data } = error.response;
        let errorMessage = formatMessage('requestError.httpError', { status });

        // 嘗試從響應數據中提取錯誤信息
        if (data) {
          if (typeof data === 'string') {
            errorMessage = data;
          } else if (data.message) {
            // 處理數組格式的錯誤信息
            if (Array.isArray(data.message)) {
              errorMessage = data.message.join('; ');
            } else {
              errorMessage = data.message;
            }
          } else if (data.error) {
            errorMessage =
              typeof data.error === 'string'
                ? data.error
                : data.error.message || formatMessage('requestError.unknown');
          } else if (data.detail) {
            errorMessage = data.detail;
          } else if (data.msg) {
            errorMessage = data.msg;
          } else if (data.errorMessage) {
            errorMessage = data.errorMessage;
          }
        }

        // 處理特定的錯誤類型
        if (errorMessage.includes('foreign key constraint fails')) {
          if (errorMessage.includes('region')) {
            errorMessage = formatMessage('requestError.foreignKey.region');
          } else if (errorMessage.includes('brand')) {
            errorMessage = formatMessage('requestError.foreignKey.brand');
          } else {
            errorMessage = formatMessage('requestError.foreignKey.generic');
          }
        }

        if (errorMessage.includes('Payment request already pending')) {
          errorMessage = formatMessage('requestError.paymentRequestPending');
        }

        const bankValidationPrefixes = [
          '收款人銀行資料不完整或格式錯誤',
          '收款人银行资料不完整或格式错误',
        ];
        const matchedBankPrefix = bankValidationPrefixes.find((prefix) =>
          errorMessage.startsWith(prefix),
        );
        if (matchedBankPrefix) {
          const remainder = errorMessage.slice(matchedBankPrefix.length).replace(/^[:：]\s*/, '');
          const localizedPrefix = formatMessage(
            'purchaseOrder.payment.airwallex.retryError.bankValidation.title',
          );
          errorMessage = remainder ? `${localizedPrefix}: ${remainder}` : localizedPrefix;
        }

        errorMessage = getApiErrorDisplayMessage(data, errorMessage);

        const isDuplicateBarcodeError =
          error.config?.url?.includes('/api/product') &&
          /barcode\s+[^\s]+\s+already exists/i.test(errorMessage);

        // 根據狀態碼提供更具體的錯誤信息
        switch (status) {
          case 400:
            if (!isDuplicateBarcodeError) {
              message.error({
                key: error.config?.url,
                content: errorMessage || formatMessage('requestError.badRequest'),
              });
            }
            break;
          case 401: {
            const currentPath = window.location.pathname;
            const isFranchiseRoute = currentPath.startsWith('/franchise/');
            const isMiRoute = currentPath.startsWith('/mi/');
            const terminalSessionCodes = new Set([
              'SESSION_REPLACED',
              'SESSION_ENDED',
              'SESSION_EXPIRED',
              'SESSION_REAUTH_REQUIRED',
            ]);
            const isTerminalSession = terminalSessionCodes.has(data?.code);

            // MI and franchise sessions are independent from admin auth.
            if (isFranchiseRoute || isMiRoute) {
              break;
            }

            if (
              shouldIgnoreAdminSession401({
                requestUrl: error.config?.url,
                requestHeaders: error.config?.headers,
                currentToken: authManager.getToken(AuthSystem.ADMIN),
              })
            ) {
              console.debug('[Auth] Ignoring login or stale-session 401 response');
              break;
            }

            // If user is creating a transfer order, defer force logout until after submit.
            if (isOnTransferCreatePage(currentPath) && !isTerminalSession) {
              const hasPendingLogout = hasTransferCreatePendingLogout();
              markTransferCreatePendingLogout();
              if (!hasPendingLogout) {
                message.warning('登入已過期，請先提交調撥單，提交後將自動登出');
              }
              break;
            }

            // For 401 errors (token expiry, invalid token), proceed with logout
            // Handle 401 immediately and synchronously
            auth401Handler.handle401();

            // Also update permission store
            import('@/stores/permissionStore').then(({ usePermissionStore }) => {
              usePermissionStore.getState().handle401Error();
            });

            const loginPath = '/user/login';

            // Prevent redirect loops
            const redirectKey = 'auth_redirect_timestamp';
            const lastRedirect = sessionStorage.getItem(redirectKey);
            const now = Date.now();

            // If we redirected in the last 2 seconds, don't redirect again
            if (lastRedirect && now - parseInt(lastRedirect) < 2000) {
              break;
            }

            // Only show error once and not on login page
            if (currentPath !== loginPath) {
              // Use a flag to prevent multiple error messages
              const errorShownKey = 'auth_error_shown';
              const errorShown = sessionStorage.getItem(errorShownKey);
              if (!errorShown || now - parseInt(errorShown) > 5000) {
                const logoutMessage = isTerminalSession
                  ? errorMessage
                  : formatMessage('requestError.sessionExpired');
                message.error(logoutMessage);
                if (isTerminalSession) {
                  storeForceLogoutNotice(logoutMessage);
                }
                sessionStorage.setItem(errorShownKey, now.toString());
              }
            }

            // Clear the admin session through the single auth boundary so the
            // permission store and refresh timers are reset consistently.
            authManager.clearAuth(AuthSystem.ADMIN);

            // 只在 SPA 中導航至登入頁，避免整頁重新載入
            if (currentPath !== loginPath) {
              sessionStorage.setItem(redirectKey, now.toString());
              const currentLocation = `${window.location.pathname}${window.location.search}${window.location.hash}`;
              const loginTarget = `${loginPath}?redirect=${encodeURIComponent(currentLocation)}`;
              setTimeout(() => {
                if (history) {
                  history.push(loginTarget);
                } else {
                  window.location.replace(loginTarget);
                }
              }, 100);
            }
            break;
          }
          case 403:
            // Always show 403 errors - permissions should be fully controlled by frontend
            // based on role_permissions table and JSON config
            // Parse detailed permission error if available
            if (errorMessage.includes('Insufficient permissions')) {
              message.error({
                content: errorMessage,
                duration: 5,
                style: {
                  marginTop: '20vh',
                },
              });
            } else {
              message.error(formatMessage('requestError.permissionDenied'));
            }
            break;
          case 404: {
            // Check if this is a permission-related endpoint on login page
            const isPermissionRequest =
              error.config?.url?.includes('/permissions') ||
              error.config?.url?.includes('/auth/permissions');
            const isLoginPage = window.location.pathname === '/user/login';

            // Check if this is the product sales date range endpoint
            const isProductSalesDateRange = error.config?.url?.includes(
              '/product-sales-date-range',
            );

            // Don't show error for permission endpoints on login page or product sales date range
            if (!isPermissionRequest || !isLoginPage) {
              if (!isProductSalesDateRange) {
                message.error(formatMessage('requestError.notFound'));
              }
            }
            break;
          }
          case 422:
            message.error(`${formatMessage('requestError.validationFailed')}: ${errorMessage}`);
            break;
          case 500: {
            // Check if this is a product API request that actually returned data
            const isProductApi = error.config?.url?.includes('/api/product');
            const hasDataInResponse =
              error.response?.data?.data && Array.isArray(error.response.data.data);

            // Don't show error if product API returned valid data
            if (isProductApi && hasDataInResponse) {
              console.log(
                'Product API returned data despite 500 status, suppressing error message',
              );
              break;
            }

            message.error(`${formatMessage('requestError.serverError')}: ${errorMessage}`);
            break;
          }
          default:
            message.error(`${errorMessage}`);
        }
      } else if (error.request) {
        // Network-shaped request errors are handled above. Keep this branch
        // for unusual request failures that include a request object.
        message.error(formatMessage('requestError.generic'));
      } else {
        // 发送请求时出了点问题
        const errorMessage = error.message || formatMessage('requestError.generic');
        message.error(errorMessage);
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: any) => {
      const url = config.url;

      // Check if we should block this request
      if (!auth401Handler.shouldAllowRequest(url)) {
        // Cancel the request by rejecting with a special error
        const cancelError = new Error('Request cancelled due to 401 handling');
        (cancelError as any).isCancel = true;
        return Promise.reject(cancelError);
      }

      // Register the request
      auth401Handler.registerRequest(url);

      // Debug: Log the actual request details for analytics endpoints
      if (isDev && url && url.includes('/analytics/')) {
        console.log('[Request Interceptor] Analytics request:', {
          url: config.url,
          method: config.method,
          params: config.params,
          paramsSerializer: config.paramsSerializer,
          fullUrl:
            config.url + (config.params ? '?' + new URLSearchParams(config.params).toString() : ''),
        });
      }

      return config;
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      // Unregister the request
      if (response.config?.url) {
        auth401Handler.unregisterRequest(response.config.url);
      }

      // 拦截响应数据，进行个性化处理
      const { data } = response;

      // 開發模式下顯示響應詳情 - 已移除以保持控制台清潔

      // 檢查響應數據結構，如果有錯誤則處理
      if (data && typeof data === 'object') {
        // 如果後端返回了success字段為false
        const responseData = data as any;
        if (responseData.success === false) {
          // 業務錯誤由errorHandler處理
        }
      }

      return response;
    },
  ],
};
